import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { BarChart3, CalendarClock, Car, CheckCircle, Clock, Map, TrendingUp, Users, Wallet, XCircle } from 'lucide-react';
import { SkeletonDashboard, statusColor, statusLabel, Stat, Badge } from '../ui/kit';
// PARTNER-UX-HEUTE-AKTEN-EINHEIT-001 (Phase 2): Handlungsliste in EIGENER Datei —
// diese Seite ist von LEGAL-HUB beansprucht, deshalb hier nur die Einhäng-Zeilen.
import TodayPanel from '../components/TodayPanel';
// ZEIT-EINHEIT-PORTAL-001: `created_at` ist SQLite-UTC ohne Kennung — roh gelesen zeigte
// diese Spalte zwei Stunden zu frueh an.
import { datumUndZeit } from '../lib/zeit';

const eur = (n) => `${(Number(n) || 0).toFixed(2)} €`;

// Stat kommt jetzt zentral aus ../ui/kit (identisches Design).

const QuickTile = ({ icon: Icon, label, onClick }) => {
  const [h, setH] = useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ background: '#fff', border: `1px solid ${h ? '#0a0a0a' : '#e5e7eb'}`, borderRadius: 12, padding: '14px 8px', textAlign: 'center', cursor: 'pointer', transition: 'border-color .15s' }}>
      <Icon size={20} color="#111" />
      <div style={{ fontSize: 12, marginTop: 7, color: '#4b5563', fontWeight: 600 }}>{label}</div>
    </div>
  );
};

const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, marginBottom: 18 };
const sectionLabel = { fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 };

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [cockpit, setCockpit] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = () => {
      api.get('/partners/dashboard').then(r => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));   // Fehler → nicht ewig Skeleton
      api.get('/partners/cockpit').then(r => setCockpit(r.data)).catch(() => {});
    };
    load();
    const iv = setInterval(load, 7000);   // live, ohne manuelles Aktualisieren
    return () => clearInterval(iv);
  }, []);

  if (loading) return <SkeletonDashboard />;
  if (!data) return null;
  const { stats, drivers, recent_rides } = data;
  const today = stats.today || {}, week = stats.week || {};
  // statusLabel / statusColor kommen jetzt zentral aus ../ui/kit

  return (
    <div>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 style={{ fontSize: 'var(--vg-fs-title)', fontWeight: 900, letterSpacing: -0.5, margin: 0 }}>Übersicht</h1>
        <span style={{ fontSize: 12, color: '#9C27B0', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#9C27B0', boxShadow: '0 0 6px #9C27B0' }} /> Live
        </span>
      </div>

      {/* Flotten-Ampel */}
      {cockpit && cockpit.status && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {cockpit.status.map((s, i) => {
            const c = s.status === 'ok' ? '#0a0a0a' : s.status === 'warning' ? '#4b5563' : '#ef4444';
            return (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, padding: '5px 11px', borderRadius: 8, background: `${c}18`, color: c }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: c }} />{s.label}
              </span>
            );
          })}
        </div>
      )}

      {/* PARTNER-UX Phase 2: die frühere „Hinweise & Warnungen"-Box lebt jetzt als
          klickbare Zeilen in der Handlungsliste weiter (TodayPanel). */}
      <TodayPanel cockpit={cockpit} warnings={data.warnings} />

      {/* Heute */}
      <div style={sectionLabel}>Heute · Betriebstag 04:00–03:59</div>
      <div style={grid}>
        <Stat label="Partnerumsatz (Flotte)" value={eur(today.revenue)} sub="aus nachträglich unveränderbaren Buchungen" icon={TrendingUp} onClick={() => navigate('/earnings')} />
        <Stat label="Kontobewegung bei VOOGO" value={eur(today.netto)} sub="vor Wochenabschluss" color="#0a0a0a" bg="#f3f4f6" onClick={() => navigate('/balance')} />
        {cockpit?.fleet?.bonus_today > 0 && (
          <Stat label="Boni an Fahrer" value={eur(cockpit.fleet.bonus_today)} sub="Partner → Fahrer, außerhalb VOOGO" color="#4b5563" bg="#f3f4f6" onClick={() => navigate('/promotions')} />
        )}
        <Stat label="Fahrten" value={today.count || 0} icon={Car} onClick={() => navigate('/reports')} />
        <Stat label="Annahmequote" value={stats.acceptance_rate != null ? `${stats.acceptance_rate} %` : '–'} icon={CheckCircle} color="#0a0a0a" bg="#f3f4f6" onClick={() => navigate('/performance')} />
        <Stat label="Fahrer online" value={`${stats.online_drivers}/${stats.total_drivers}`} icon={Users} color="#0a0a0a" bg="#f3f4f6" onClick={() => navigate('/drivers')} />
      </div>

      {/* Diese Woche */}
      <div style={sectionLabel}>Diese Woche · Mo 04:00–Mo 03:59</div>
      <div style={grid}>
        <Stat label="Partnerumsatz (Woche)" value={eur(week.revenue)} sub="aus nachträglich unveränderbaren Buchungen" icon={TrendingUp} />
        <Stat label="Kontobewegung bei VOOGO" value={eur(week.netto)} sub="noch keine Auszahlung" color="#0a0a0a" bg="#f3f4f6" />
        {cockpit?.fleet?.bonus_week > 0 && (
          <Stat label="Boni an Fahrer" value={eur(cockpit.fleet.bonus_week)} sub="Partner → Fahrer, außerhalb VOOGO" color="#4b5563" bg="#f3f4f6" onClick={() => navigate('/promotions')} />
        )}
        <Stat label="Fahrten" value={week.count || 0} icon={Car} />
        <Stat label="Umsatz / Fahrzeug" value={eur(stats.revenue_per_vehicle_week)} icon={TrendingUp} />
        <Stat label="Lenkstunden / Fahrzeug" value={`${stats.hours_per_vehicle_week || 0} h`} sub={`${stats.hours_week || 0} h gesamt`} icon={Clock} />
      </div>

      {/* Schnellzugriff */}
      <div style={sectionLabel}>Schnellzugriff</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 10, marginBottom: 18 }}>
        <QuickTile icon={Users} label="Fahrer" onClick={() => navigate('/drivers')} />
        <QuickTile icon={Car} label="Fahrzeuge" onClick={() => navigate('/vehicles')} />
        <QuickTile icon={Wallet} label="Abrechnung" onClick={() => navigate('/settlements')} />
        <QuickTile icon={CalendarClock} label="Reservierungen" onClick={() => navigate('/reservations')} />
        <QuickTile icon={Map} label="Live-Karte" onClick={() => navigate('/live-map')} />
        <QuickTile icon={BarChart3} label="Berichte" onClick={() => navigate('/reports')} />
      </div>

      {/* Meine Fahrer (live) */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 16, border: '1px solid #e5e7eb', marginBottom: 18 }}>
        <h3 style={{ fontSize: 13, fontWeight: 800, marginTop: 0, marginBottom: 12 }}>Meine Fahrer</h3>
        {drivers.length === 0 && <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Noch keine Fahrer</p>}
        {drivers.map(d => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.is_online ? '#9C27B0' : '#d1d5db', boxShadow: d.is_online ? '0 0 6px #9C27B0' : 'none', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}><CheckCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {d.rides_accepted || 0} · <XCircle size={13} style={{ verticalAlign: -2, marginRight: 6 }} /> {d.rides_rejected || 0}</div>
            </div>
            <div style={{ fontSize: 12, color: d.is_online ? '#9C27B0' : '#6b7280', fontWeight: 700, flexShrink: 0 }}>{d.is_online ? 'Online' : 'Offline'}</div>
          </div>
        ))}
      </div>

      {/* Letzte Fahrten (live) */}
      <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <div style={{ padding: '13px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ fontSize: 13, fontWeight: 800, margin: 0 }}>Letzte Fahrten</h3>
          <span style={{ fontSize: 12, color: '#6b7280' }}>Live</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="vg-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb' }}>
              {['Fahrer','Kunde','Strecke','Kundenfahrpreis','Kundeneinzug Bar','Status','Zeit'].map(h => (
                <th key={h} style={{ padding: '10px 14px', fontSize: 12, color: '#6b7280', fontWeight: 700, textAlign: 'left', textTransform: 'uppercase', letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {recent_rides.map((r, i) => (
                <tr key={r.id} style={{ borderTop: '1px solid #f3f4f6', background: i%2===0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap' }}>{r.driver_name||'–'}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#4b5563', whiteSpace: 'nowrap' }}>{r.customer_name}</td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: '#4b5563', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.pickup_address} → {r.dropoff_address}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>{eur(r.price)}</td>
                  <td style={{ padding: '11px 14px', fontSize: 13, color: '#0a0a0a', whiteSpace: 'nowrap' }}>{eur(r.cash_collected)}</td>
                  <td style={{ padding: '11px 14px' }}>
                    <Badge color={statusColor[r.status]}>{statusLabel[r.status] || r.status}</Badge>
                  </td>
                  <td style={{ padding: '11px 14px', fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
                    {datumUndZeit(r.created_at)}
                  </td>
                </tr>
              ))}
              {recent_rides.length === 0 && <tr><td colSpan={7} style={{ padding: 24, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Noch keine Fahrten</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
